/**
 * The default SOS table's stand-in rows, and the small helpers it builds them with.
 * Kept out of the component file so the component file exports only components.
 */

import type { SOSOfficer } from "@/components/DefaultSOSTable";
import { generateTimeSlots } from "@/config/modeConfig";

// Generate a unique ID
let _idCounter = 1;
export const uid = () => `id_${_idCounter++}`;

// Generate counter options based on mode
export function generateCounterOptions(
  operationMode: "arrival" | "departure",
): string[] {
  const numCounters = operationMode === "arrival" ? 42 : 38;
  return Array.from({ length: numCounters }, (_, i) => String(i + 1));
}

// Build the stand-in officers for when the roster supplies none.
// Morning shift: returns empty (component is hidden for morning anyway).
// Night arrival: the fixed train positions below (their counters and times don't vary).
// Night departure: empty — see the comment on that branch.
export function buildDefaultOfficers(
  shiftType: "morning" | "night",
  operationMode: "arrival" | "departure",
): SOSOfficer[] {
  if (shiftType === "morning") return [];

  const slots = generateTimeSlots(shiftType);
  const counters = generateCounterOptions(operationMode);

  if (operationMode === "arrival") {
    return [
      {
        officerId: uid(),
        name: "SN2",
        supportSn: 2,
        deployment: "train",
        checked: true,
        counterRows: [
          { id: uid(), startTime: slots[10], endTime: slots[18], counter: counters[39] },
        ],
      },
      {
        officerId: uid(),
        name: "SN3",
        supportSn: 3,
        deployment: "train",
        checked: true,
        counterRows: [
          { id: uid(), startTime: slots[10], endTime: slots[18], counter: counters[38] },
        ],
      },
      {
        officerId: uid(),
        name: "SN4",
        supportSn: 4,
        deployment: "train",
        checked: true,
        counterRows: [
          { id: uid(), startTime: slots[10], endTime: slots[18], counter: counters[37] },
        ],
      },
      {
        officerId: uid(),
        name: "SN9",
        supportSn: 9,
        deployment: "train",
        checked: true,
        counterRows: [
          { id: uid(), startTime: slots[10], endTime: slots[18], counter: counters[36] },
        ],
      },
      {
        officerId: uid(),
        name: "SN10",
        supportSn: 10,
        deployment: "train",
        checked: true,
        counterRows: [
          { id: uid(), startTime: slots[10], endTime: slots[18], counter: counters[35] },
        ],
      },
      {
        officerId: uid(),
        name: "SN11",
        supportSn: 11,
        deployment: "train",
        checked: true,
        counterRows: [
          { id: uid(), startTime: slots[10], endTime: slots[18], counter: counters[4] },
        ],
      },
      {
        officerId: uid(),
        name: "SN12",
        supportSn: 12,
        deployment: "train",
        checked: true,
        counterRows: [
          { id: uid(), startTime: slots[10], endTime: slots[18], counter: counters[3] },
        ],
      },
      {
        officerId: uid(),
        name: "SN13",
        supportSn: 13,
        deployment: "train",
        checked: true,
        counterRows: [
          { id: uid(), startTime: slots[10], endTime: slots[18], counter: counters[2] },
        ],
      },
    ];
  }

  // operationMode === "departure"
  //
  // No fixed positions: the OWC officers come from the uploaded roster itself.
  // rosterSosToOfficers (utils/rosterSos.ts) takes every NAMED row on the OWC page —
  // a blank row is a position nobody is on tonight and is skipped, so a row is
  // ticked exactly when the PDF names someone — together with the counters and
  // times the time grid prints for them, wherever the sheet puts them tonight.
  // There used to be four placeholder rows here (SN3-6 on counters and times nobody
  // had confirmed); with a PDF they were replaced by the real ones, and without one
  // they pre-ticked invented assignments. With no roster uploaded the table starts
  // empty, and officers are added by hand.
  return [];
}
